import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { LoadingSpinner } from '../../../shared/ui';
import {
  AdminBlogShell,
  AdminBooleanToggleField,
  AdminPageHeader,
  appendYowanContentPreviewQuery,
  useAdminContentMediaOptions,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import {
  fetchYowanAdminPageContentDetail,
  getYowanAdminMutationErrorMessage,
  updateYowanAdminPageContentSlot,
  validateYowanImageUploadFile,
  YOWAN_IMAGE_UPLOAD_HELP_TEXT,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const stackedFormFieldStyle = { gridTemplateColumns: '1fr' };
const fieldNoteStyle = { margin: 0, fontSize: '0.75rem', lineHeight: 1.45 };

const buildDefaultValues = (slot = null) => ({
  adminLabel: slot?.adminLabel || '',
  adminDescription: slot?.adminDescription || '',
  title: slot?.title || '',
  body: slot?.body || '',
  displayOrder: slot?.displayOrder || 0,
  isPublished: slot?.isPublished === true,
  primary_image_id: slot?.primary_image_id || '',
});

const previewImageStyle = {
  display: 'block',
  width: 'min(100%, 22rem)',
  aspectRatio: '16 / 10',
  objectFit: 'contain',
  borderRadius: '6px',
  background: 'rgba(47, 54, 61, 0.06)',
};

const metaPanelStyle = {
  display: 'grid',
  gap: '0.35rem',
  padding: '0.85rem 1rem',
  border: '1px solid rgba(47, 54, 61, 0.12)',
  borderRadius: '6px',
  background: 'rgba(47, 54, 61, 0.035)',
};

const metaTitleStyle = {
  margin: 0,
  fontWeight: 600,
};

const metaBodyStyle = {
  margin: 0,
  color: 'rgba(47, 54, 61, 0.72)',
  fontSize: '0.9rem',
  lineHeight: 1.55,
};

const getSlotImageUrl = (slot) =>
  slot?.primary_image?.preview_url || slot?.primary_image?.thumbnail_url || slot?.primary_image?.url || '';

const AdminPageContentsEditPage = () => {
  const content = yowanPageContent['/admin/content/page-contents/:pageKey/:slotKey/edit'];
  const { pageKey = '', slotKey = '' } = useParams();
  const navigate = useNavigate();
  const preservedDomainSearch = usePreservedDomainSearch();
  const [slot, setSlot] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const {
    mediaAssetOptions,
    mediaAssetLoading,
    mediaAssetError,
  } = useAdminContentMediaOptions(slot?.usageScope || '', slot?.primary_image);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: buildDefaultValues(),
  });

  useEffect(() => {
    document.title = 'ページコンテンツ編集';
  }, []);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setSubmitMessage('');

    fetchYowanAdminPageContentDetail(pageKey, slotKey)
      .then((payload) => {
        if (isMounted) {
          setSlot(payload);
          reset(buildDefaultValues(payload));
        }
      })
      .catch(() => {
        if (isMounted) {
          setSlot(null);
          reset(buildDefaultValues());
          setSubmitMessage('ページコンテンツを読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [pageKey, reset, slotKey]);

  if (!content) {
    return null;
  }

  const imageUrl = getSlotImageUrl(slot);
  const mediaAssetField = content.form.mediaAssetField;

  const onSubmit = async (formValues) => {
    if (!slot) {
      setSubmitMessage('ページコンテンツを読み込めていないため保存できません。');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const updatedSlot = await updateYowanAdminPageContentSlot(pageKey, slotKey, {
        ...buildDefaultValues(slot),
        ...formValues,
        usageScope: slot.usageScope,
      });
      setSubmitMessage('保存しました。');
      navigate(appendYowanContentPreviewQuery('/admin/content/page-contents', preservedDomainSearch), {
        replace: true,
        state: {
          refreshPageContentsAt: Date.now(),
          savedPageContent: true,
          savedSlotKey: `${updatedSlot.pageKey}/${updatedSlot.slotKey}`,
        },
      });
    } catch (error) {
      setSubmitMessage(getYowanAdminMutationErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AdminBlogShell activeNavHref="/admin/content/page-contents" routeKey="/admin/content/page-contents/:pageKey/:slotKey/edit">
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="back-to-list" to={appendYowanContentPreviewQuery('/admin/content/page-contents', preservedDomainSearch)}>
            一覧へ戻る
          </Link>,
        ]}
      />

      <form className="yowan-admin-form c-surface-content" onSubmit={handleSubmit(onSubmit)}>
        {slot ? (
          <div style={metaPanelStyle}>
            <p style={metaTitleStyle}>{slot.pageLabel} / {slot.sectionLabel}</p>
            <p style={metaBodyStyle}>{slot.adminDescription}</p>
          </div>
        ) : null}

        {imageUrl ? (
          <img
            src={imageUrl}
            alt={slot?.primary_image?.alt_text || slot?.adminLabel || ''}
            style={previewImageStyle}
          />
        ) : null}

        <AdminBooleanToggleField
          label={content.form.labels.isPublished}
          name="isPublished"
          register={register}
        />

        <div className="c-form__field" style={stackedFormFieldStyle}>
          <span className="c-form__label">{content.form.labels.primaryImage}</span>
          <div style={{ display: 'grid', gap: '0.5rem', minWidth: 0 }}>
            {mediaAssetOptions.length ? (
              <select
                className="c-form__control"
                data-api-path={mediaAssetField?.api?.path}
                {...register('primary_image_id')}
              >
                <option value="">既存画像を使わない</option>
                {mediaAssetOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                    {option.isActive === false ? ' / 停止中' : ''}
                  </option>
                ))}
              </select>
            ) : (
              <p className="yowan-admin-form__field-note" style={fieldNoteStyle}>
                登録済み画像はありません。
              </p>
            )}
            <input
              className="c-form__control"
              type="file"
              accept="image/*"
              {...register('primary_image_upload', { validate: validateYowanImageUploadFile })}
            />
            {errors?.primary_image_upload ? (
              <em className="yowan-admin-form__error" style={fieldNoteStyle}>
                {errors.primary_image_upload.message}
              </em>
            ) : null}
            <input
              className="c-form__control"
              type="text"
              placeholder="代替テキスト"
              {...register('primary_image_alt_text')}
            />
            <input
              className="c-form__control"
              type="text"
              placeholder="キャプション"
              {...register('primary_image_caption')}
            />
          </div>
          {mediaAssetField?.helpText ? (
            <p className="yowan-admin-form__field-note" style={fieldNoteStyle}>
              {[mediaAssetField.helpText, YOWAN_IMAGE_UPLOAD_HELP_TEXT].filter(Boolean).join(' ')}
            </p>
          ) : null}
          {mediaAssetLoading || mediaAssetError ? (
            <p className="yowan-admin-form__field-note" style={fieldNoteStyle}>
              {mediaAssetLoading ? <LoadingSpinner inline size="sm" /> : mediaAssetError}
            </p>
          ) : null}
        </div>

        <div className="yowan-admin-form__actions">
          <button type="submit" className="c-form__submit" disabled={isSubmitting || isLoading || !slot}>
            {content.form.submitLabel}
          </button>
        </div>

        {isSubmitting ? (
          <p className="yowan-admin-form__message yowan-admin-form__message--pending">
            <LoadingSpinner inline size="sm" />
            <span>{content.form.pendingMessage}</span>
          </p>
        ) : submitMessage ? (
          <p className="yowan-admin-form__message">{submitMessage}</p>
        ) : null}
      </form>
    </AdminBlogShell>
  );
};

export default AdminPageContentsEditPage;
